import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { safeParseJSON, safeSaveJSONAsync, isObject, isArray } from "@/lib/safeLocalStorage";

export type MenopauseStage = "perimenopause" | "menopause" | "postmenopause";

export interface MenopauseData {
  isInMenopause: boolean;
  stage: MenopauseStage;
  startDate: string | null;
  lastPeriodDate: string | null;
  monthsSinceLastPeriod: number;
  hrtStartDate: string | null;
  isOnHRT: boolean;
}

export interface MenopauseDailyLog {
  date: string;
  hotFlashes: number;
  nightSweats: boolean;
  moodSwings: string[];
  sleepQuality: number;
  vaginalDryness: boolean;
  headaches: boolean;
  jointPain: boolean;
  fatigue: number;
  brainFog: boolean;
  anxietyLevel: number;
  irritability: number;
  weight: number | null;
  bloodPressure: string;
  notes: string;
}

const STORAGE_KEY = "menopauseData";
const LOGS_KEY = "menopauseLogs";

const defaultData: MenopauseData = {
  isInMenopause: false,
  stage: "perimenopause",
  startDate: null,
  lastPeriodDate: null,
  monthsSinceLastPeriod: 0,
  hrtStartDate: null,
  isOnHRT: false,
};

const isValidMenopauseData = (data: unknown): data is MenopauseData => {
  if (!isObject(data)) return false;
  const obj = data as Record<string, unknown>;
  return typeof obj.isInMenopause === "boolean";
};

const isValidMenopauseLogs = (data: unknown): data is MenopauseDailyLog[] => {
  return isArray(data) && data.every((l) =>
    typeof l === "object" && l !== null &&
    typeof (l as MenopauseDailyLog).date === "string"
  );
};

interface MenopauseContextType {
  menopauseData: MenopauseData;
  logs: MenopauseDailyLog[];
  setMenopauseData: (data: Partial<MenopauseData>) => void;
  enableMenopauseMode: (stage: MenopauseStage, lastPeriodDate?: string) => void;
  disableMenopauseMode: () => void;
  addLog: (log: MenopauseDailyLog) => void;
  getStageInfo: () => { title: string; description: string; tips: string[] };
}

const MenopauseContext = createContext<MenopauseContextType | undefined>(undefined);

export const MenopauseProvider = ({ children }: { children: ReactNode }) => {
  const [menopauseData, setMenopauseDataState] = useState<MenopauseData>(() => {
    return safeParseJSON(STORAGE_KEY, defaultData, isValidMenopauseData);
  });

  const [logs, setLogs] = useState<MenopauseDailyLog[]>(() => {
    return safeParseJSON(LOGS_KEY, [], isValidMenopauseLogs);
  });

  useEffect(() => {
    void safeSaveJSONAsync(STORAGE_KEY, menopauseData);
  }, [menopauseData]);

  useEffect(() => {
    void safeSaveJSONAsync(LOGS_KEY, logs);
  }, [logs]);

  useEffect(() => {
    if (menopauseData.lastPeriodDate) {
      const lastPeriod = new Date(menopauseData.lastPeriodDate);
      const now = new Date();
      const months = Math.floor(
        (now.getTime() - lastPeriod.getTime()) / (1000 * 60 * 60 * 24 * 30)
      );
      setMenopauseDataState((prev) => ({
        ...prev,
        monthsSinceLastPeriod: months,
      }));
    }
  }, [menopauseData.lastPeriodDate]);

  const setMenopauseData = (data: Partial<MenopauseData>) => {
    setMenopauseDataState((prev) => ({ ...prev, ...data }));
  };

  const enableMenopauseMode = (stage: MenopauseStage, lastPeriodDate?: string) => {
    setMenopauseDataState({
      ...menopauseData,
      isInMenopause: true,
      stage,
      startDate: new Date().toISOString(),
      lastPeriodDate: lastPeriodDate || null,
    });
  };

  const disableMenopauseMode = () => {
    setMenopauseDataState({
      ...defaultData,
    });
  };

  const addLog = (log: MenopauseDailyLog) => {
    setLogs((prev) => {
      const filtered = prev.filter((l) => l.date !== log.date);
      return [...filtered, log].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );
    });
  };

  const getStageInfo = () => {
    switch (menopauseData.stage) {
      case "perimenopause":
        return {
          title: "Perimenopause",
          description:
            "The transition phase before menopause when hormone levels begin to fluctuate. Periods may become irregular.",
          tips: [
            "Track your cycle patterns to identify changes",
            "Monitor hot flashes and night sweats frequency",
            "Prioritize sleep hygiene",
            "Consider speaking with your doctor about symptoms",
          ],
        };
      case "menopause":
        return {
          title: "Menopause",
          description:
            "Officially reached after 12 consecutive months without a period. Average age is 51.",
          tips: [
            "Stay active with weight-bearing exercises",
            "Focus on calcium and vitamin D intake",
            "Practice stress management techniques",
            "Discuss HRT options with your healthcare provider",
          ],
        };
      case "postmenopause":
        return {
          title: "Postmenopause",
          description:
            "The years after menopause. Symptoms may ease, but health monitoring remains important.",
          tips: [
            "Continue bone health monitoring",
            "Maintain heart-healthy habits",
            "Stay socially connected",
            "Regular health screenings are essential",
          ],
        };
    }
  };

  return (
    <MenopauseContext.Provider
      value={{
        menopauseData,
        logs,
        setMenopauseData,
        enableMenopauseMode,
        disableMenopauseMode,
        addLog,
        getStageInfo,
      }}
    >
      {children}
    </MenopauseContext.Provider>
  );
};

export const useMenopauseMode = () => {
  const context = useContext(MenopauseContext);
  if (!context) {
    throw new Error("useMenopauseMode must be used within MenopauseProvider");
  }
  return context;
};
